import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect, useCallback } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';
import { API, Badge, tableStyle, thStyle, tdStyle, inputStyle, btnPrimary, btnSecondary, btnSmall, overlayStyle, modalStyle, FormRow, DetailField } from './AcademyShared';

const STATUSES = ['scheduled', 'graded', 'cancelled'];

export default function AcademyAssessments() {
  const companyId = localStorage.getItem('selectedCompanyId');
  const [list, setList] = useState([]);
  const [cohorts, setCohorts] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [status, setStatus] = useState('');
  const [cohortFilter, setCohortFilter] = useState('');
  const [editing, setEditing] = useState(null);
  const [grading, setGrading] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/companies/${companyId}/academy/assessments?per_page=50`;
      if (status) url += `&status=${status}`;
      if (cohortFilter) url += `&cohort_id=${cohortFilter}`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      const json = await res.json();
      setList(Array.isArray(json.data) ? json.data : []);
    } catch (e) { console.error(e); setList([]); }
    setLoading(false);
  }, [companyId, status, cohortFilter]);

  useEffect(() => { fetchList(); }, [fetchList]);

  useEffect(() => {
    fetch(`${API}/companies/${companyId}/academy/cohorts?per_page=500&status=active`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : { data: [] }).then(j => setCohorts(Array.isArray(j.data) ? j.data : [])).catch(() => setCohorts([]));
  }, [companyId]);

  const changeStatus = async (id, newStatus) => {
    const res = await fetch(`${API}/companies/${companyId}/academy/assessments/${id}/status`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: newStatus }),
    });
    if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Status change failed'); return; }
    fetchList();
  };

  const removeItem = async (id) => {
    if (!window.confirm('Delete this assessment?')) return;
    const res = await fetch(`${API}/companies/${companyId}/academy/assessments/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
    if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Cannot delete'); return; }
    fetchList();
  };

  return (
    <div style={{ padding: '24px 32px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#1a1a2e' }}>📊 Assessments</h2>
        <button style={btnPrimary} onClick={() => setEditing({})}>+ New Assessment</button>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        <select style={{ ...inputStyle, width: 220 }} value={cohortFilter} onChange={e => setCohortFilter(e.target.value)}>
          <option value="">All Cohorts</option>
          {cohorts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select style={{ ...inputStyle, width: 160 }} value={status} onChange={e => setStatus(e.target.value)}>
          <option value="">All Statuses</option>
          {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead><tr>{['Title', 'Cohort', 'Weight', 'Status', 'Actions'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
            <tbody>
              {initialLoading && <tr><td colSpan={5} style={{ ...tdStyle, textAlign: 'center' }}>Loading...</td></tr>}
              {!loading && list.length === 0 && <tr><td colSpan={5} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>No assessments</td></tr>}
              {list.map(a => (
                <tr key={a.id}>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{a.title}</td>
                  <td style={tdStyle}>{a.cohort?.name || '—'}</td>
                  <td style={tdStyle}>{a.weight}%</td>
                  <td style={tdStyle}><Badge value={a.status} /></td>
                  <td style={tdStyle}>
                    <button style={{ ...btnSmall, marginRight: 6 }} onClick={() => setEditing(a)}>Edit</button>
                    <button style={{ ...btnSmall, marginRight: 6 }} onClick={() => setGrading(a)}>Grade</button>
                    {a.status !== 'cancelled' && <button style={{ ...btnSmall, marginRight: 6 }} onClick={() => changeStatus(a.id, 'cancelled')}>Cancel</button>}
                    {a.status === 'cancelled' && <button style={{ ...btnSmall, marginRight: 6 }} onClick={() => changeStatus(a.id, 'scheduled')}>Reinstate</button>}
                    <button style={{ ...btnSmall, color: '#ef4444' }} onClick={() => removeItem(a.id)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && <AssessmentModal companyId={companyId} cohorts={cohorts} item={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); fetchList(); }} />}
      {grading && <GradingModal companyId={companyId} assessment={grading} onClose={() => setGrading(null)} onSaved={() => { setGrading(null); fetchList(); }} />}
    </div>
  );
}

function AssessmentModal({ companyId, cohorts, item, onClose, onSaved }) {
  const isNew = !item.id;
  const [form, setForm] = useState({
    cohort_id: item.cohort_id || '', level_id: item.level_id || '', title: item.title || '',
    assessment_type: item.assessment_type || '', weight: item.weight || '', assessment_date: item.assessment_date || '',
    notes: item.notes || '',
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.title.trim() || !form.cohort_id) { alert('Title and Cohort are required'); return; }
    setSaving(true);
    try {
      const url = isNew ? `${API}/companies/${companyId}/academy/assessments` : `${API}/companies/${companyId}/academy/assessments/${item.id}`;
      const res = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Save failed'); setSaving(false); return; }
      onSaved();
    } catch (e) { console.error(e); setSaving(false); }
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={e => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>{isNew ? 'New Assessment' : 'Edit Assessment'}</h3>
        <FormRow label="Cohort *">
          <select style={inputStyle} value={form.cohort_id} onChange={e => setForm({ ...form, cohort_id: e.target.value })}>
            <option value="">Select cohort</option>
            {cohorts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </FormRow>
        <FormRow label="Title *"><input style={inputStyle} value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} /></FormRow>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <FormRow label="Type">
            <select style={inputStyle} value={form.assessment_type} onChange={e => setForm({ ...form, assessment_type: e.target.value })}>
              <option value="">Select type</option>
              {['quiz', 'project', 'exam', 'assignment'].map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </FormRow>
          <FormRow label="Weight (%)"><input type="number" style={inputStyle} value={form.weight} onChange={e => setForm({ ...form, weight: e.target.value })} /></FormRow>
        </div>
        <FormRow label="Assessment Date"><input type="date" style={inputStyle} value={form.assessment_date} onChange={e => setForm({ ...form, assessment_date: e.target.value })} /></FormRow>
        <FormRow label="Notes"><textarea style={{ ...inputStyle, minHeight: 60 }} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></FormRow>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
          <button style={btnSecondary} onClick={onClose}>Cancel</button>
          <button style={btnPrimary} onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}

function GradingModal({ companyId, assessment, onClose, onSaved }) {
  const [enrollments, setEnrollments] = useState([]);
  const [grades, setGrades] = useState({});
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [enrRes, aRes] = await Promise.all([
          fetch(`${API}/companies/${companyId}/academy/enrollments?cohort_id=${assessment.cohort_id}&status=active&per_page=500`, { headers: getAuthHeaders() }),
          fetch(`${API}/companies/${companyId}/academy/assessments/${assessment.id}`, { headers: getAuthHeaders() }),
        ]);
        const json = await enrRes.json();
        const list = Array.isArray(json.data) ? json.data : [];
        setEnrollments(list);
        const aJson = aRes.ok ? await aRes.json() : {};
        const existing = {};
        (aJson.grades || []).forEach(g => { existing[g.enrollment_id] = g.score; });
        setGrades(existing);
      } catch (e) { console.error(e); }
      setLoading(false);
    })();
  }, [companyId, assessment]);

  const saveGrades = async () => {
    setSaving(true);
    try {
      const gradeRecords = enrollments.map(en => ({ enrollment_id: en.id, score: grades[en.id] !== undefined && grades[en.id] !== '' ? Number(grades[en.id]) : null })).filter(r => r.score !== null);
      const res = await fetch(`${API}/companies/${companyId}/academy/assessments/${assessment.id}/grades`, {
        method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ grades: gradeRecords }),
      });
      if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Save failed'); setSaving(false); return; }
      onSaved();
    } catch (e) { console.error(e); setSaving(false); }
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={{ ...modalStyle, width: 560 }} onClick={e => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>Grade: {assessment.title} <span style={{ fontSize: 12, color: '#9ca3af' }}>(out of 20)</span></h3>
        {initialLoading ? <div>Loading...</div> : (
          <table style={tableStyle}>
            <thead><tr>{['Student', 'Score /20'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
            <tbody>
              {enrollments.length === 0 && <tr><td colSpan={2} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>No enrolled students</td></tr>}
              {enrollments.map(en => (
                <tr key={en.id}>
                  <td style={tdStyle}>{en.student ? en.student.full_name : '—'}</td>
                  <td style={tdStyle}>
                    <input type="number" min="0" max="20" step="0.5" style={{ ...inputStyle, width: 90 }}
                      value={grades[en.id] ?? ''} onChange={e => setGrades({ ...grades, [en.id]: e.target.value })} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
          <button style={btnSecondary} onClick={onClose}>Cancel</button>
          <button style={btnPrimary} onClick={saveGrades} disabled={saving}>{saving ? 'Saving...' : 'Save Grades'}</button>
        </div>
      </div>
    </div>
  );
}
